/**
 * Interview Loop config — Scorecard Templates + Interview Plans.
 *
 * HR / hiring leaders manage the company's interview loop blueprints here.
 * Scorecard templates define the questions interviewers fill out per round.
 * Interview Plans are reusable round blueprints attached to a requisition
 * (defines round order + which scorecard template each round uses).
 */
import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Trash, Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const QTYPES = [
  { value: "rating_5",       label: "Rating (1–5)" },
  { value: "yes_no",         label: "Yes / No" },
  { value: "text",           label: "Free text" },
  { value: "single_select",  label: "Single select" },
  { value: "multi_select",   label: "Multi select" },
  { value: "recommendation", label: "Overall recommendation (required)" },
];

const ROUND_TYPES = [
  { value: "screen",     label: "Screen" },
  { value: "tech",       label: "Technical" },
  { value: "manager",    label: "Manager" },
  { value: "bar_raiser", label: "Bar Raiser" },
  { value: "culture",    label: "Culture" },
  { value: "final",      label: "Final / Founder" },
  { value: "other",      label: "Other" },
];

const ROUND_TYPE_COLOR = {
  screen:     "bg-slate-100 text-slate-700",
  tech:       "bg-blue-100 text-blue-700",
  manager:    "bg-violet-100 text-violet-700",
  bar_raiser: "bg-amber-100 text-amber-800",
  culture:    "bg-emerald-100 text-emerald-700",
  final:      "bg-rose-100 text-rose-700",
  other:      "bg-zinc-100 text-zinc-700",
};

const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

export default function InterviewLoop() {
  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="interview-loop-page">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Interview Loop</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Scorecard templates and interview plans — the structured backbone of your hiring loop.
            All settings are tenant-isolated.
          </p>
        </header>
        <Tabs defaultValue="templates">
          <TabsList>
            <TabsTrigger value="templates" data-testid="tab-templates">Scorecard templates</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">Interview plans</TabsTrigger>
          </TabsList>
          <TabsContent value="templates" className="mt-4"><TemplatesPanel /></TabsContent>
          <TabsContent value="plans" className="mt-4"><PlansPanel /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

/* ---------------------- Templates panel ---------------------- */
function TemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // template object or "new"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/interview-loop/scorecard-templates");
      setTemplates(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSaved = (t, isNew) => {
    setTemplates(ts => isNew ? [...ts, t] : ts.map(x => x.id === t.id ? t : x));
    setEditing(null);
    toast.success(`Template ${isNew ? "created" : "saved"}`);
  };
  const onDeleted = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await api.delete(`/interview-loop/scorecard-templates/${t.id}`);
      setTemplates(ts => ts.filter(x => x.id !== t.id));
      toast.success("Template removed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading) return <Card className="p-6 text-sm text-zinc-500">Loading…</Card>;

  return (
    <Card className="p-6" data-testid="templates-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold">Scorecard templates</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            9 system defaults seeded. Edit the questions or create custom templates per round type.
          </p>
        </div>
        <Button onClick={() => setEditing("new")} data-testid="tpl-new-btn">
          <Plus size={16} className="mr-1" /> New template
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b">
            <th className="pb-2 font-medium">Name</th>
            <th className="pb-2 font-medium">Round type</th>
            <th className="pb-2 font-medium">Questions</th>
            <th className="pb-2 font-medium">Source</th>
            <th className="pb-2 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {templates.map(t => (
            <tr key={t.id} className="border-b last:border-0" data-testid={`tpl-row-${t.key}`}>
              <td className="py-3 pr-2">
                <div className="font-medium">{t.name}</div>
                {t.description && <div className="text-xs text-zinc-500 truncate max-w-md">{t.description}</div>}
              </td>
              <td className="py-3 pr-2">
                <span className={`text-xs px-2 py-0.5 rounded ${ROUND_TYPE_COLOR[t.round_type] || ROUND_TYPE_COLOR.other}`}>
                  {ROUND_TYPES.find(r => r.value === t.round_type)?.label || t.round_type}
                </span>
              </td>
              <td className="py-3 pr-2">{t.questions?.length || 0}</td>
              <td className="py-3 pr-2">
                {t.is_system ? <Badge variant="outline" className="text-[10px]">System</Badge> : <Badge variant="outline" className="text-[10px] bg-emerald-50">Custom</Badge>}
              </td>
              <td className="py-3 text-right space-x-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`tpl-edit-${t.key}`}>
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
                {!t.is_system && (
                  <Button size="sm" variant="outline" onClick={() => onDeleted(t)} data-testid={`tpl-del-${t.key}`}>
                    <Trash size={14} />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && (
        <TemplateEditor
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </Card>
  );
}

function TemplateEditor({ template, onClose, onSaved }) {
  const isNew = !template;
  const [name, setName] = useState(template?.name || "");
  const [roundType, setRoundType] = useState(template?.round_type || "other");
  const [description, setDescription] = useState(template?.description || "");
  const [questions, setQuestions] = useState(() => {
    if (template?.questions) return JSON.parse(JSON.stringify(template.questions));
    return [
      { id: "overall", label: "Overall rating", type: "rating_5", required: true },
      { id: "notes",   label: "Notes",          type: "text",     required: false },
      { id: "recommendation", label: "Overall recommendation", type: "recommendation", required: true },
    ];
  });
  const [saving, setSaving] = useState(false);

  const addQuestion = () => {
    const newId = `q_${Date.now().toString(36).slice(-5)}`;
    // Insert before the recommendation question if present
    const recIdx = questions.findIndex(q => q.type === "recommendation");
    const newQ = { id: newId, label: "New question", type: "rating_5", required: false };
    const next = [...questions];
    if (recIdx >= 0) next.splice(recIdx, 0, newQ); else next.push(newQ);
    setQuestions(next);
  };
  const removeQuestion = (idx) => setQuestions(qs => qs.filter((_, i) => i !== idx));
  const updateQuestion = (idx, patch) => setQuestions(qs => qs.map((q, i) => i === idx ? { ...q, ...patch } : q));

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!questions.some(q => q.type === "recommendation")) {
      toast.error("Every template needs an Overall Recommendation question");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        round_type: roundType,
        description: description || null,
        questions: questions.map(q => ({
          id: q.id, label: q.label, type: q.type,
          required: !!q.required,
          options: ["single_select", "multi_select"].includes(q.type) ? (q.options || []) : null,
        })),
        is_active: true,
      };
      const { data } = isNew
        ? await api.post("/interview-loop/scorecard-templates", payload)
        : await api.put(`/interview-loop/scorecard-templates/${template.id}`, payload);
      onSaved(data, isNew);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tpl-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? "New scorecard template" : `Edit — ${template.name}`}</DialogTitle>
          <DialogDescription>
            Define the questions interviewers will fill after the round. Drag to reorder later (coming soon).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="tpl-name" />
            </div>
            <div>
              <Label className="text-xs">Round type</Label>
              <select
                value={roundType}
                onChange={(e) => setRoundType(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded text-sm"
                data-testid="tpl-round-type"
              >
                {ROUND_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="tpl-desc" />
          </div>

          <div className="border border-zinc-200 rounded">
            <div className="flex items-center justify-between p-3 border-b bg-zinc-50">
              <div className="text-sm font-medium">Questions ({questions.length})</div>
              <Button size="sm" variant="outline" onClick={addQuestion} data-testid="tpl-add-q"><Plus size={14} className="mr-1"/> Add question</Button>
            </div>
            <div className="divide-y">
              {questions.map((q, idx) => (
                <div key={q.id} className="p-3 space-y-2" data-testid={`tpl-q-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-5">
                      <Label className="text-[10px] uppercase text-zinc-500">Question</Label>
                      <Input value={q.label} onChange={(e) => updateQuestion(idx, { label: e.target.value })} data-testid={`tpl-q-label-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase text-zinc-500">Type</Label>
                      <select
                        value={q.type}
                        onChange={(e) => updateQuestion(idx, { type: e.target.value })}
                        disabled={q.type === "recommendation"}
                        className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded text-sm"
                        data-testid={`tpl-q-type-${idx}`}
                      >
                        {QTYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2 pt-5">
                      <label className="text-xs flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!q.required}
                          onChange={(e) => updateQuestion(idx, { required: e.target.checked })}
                          disabled={q.type === "recommendation"}
                          data-testid={`tpl-q-req-${idx}`}
                        />
                        Required
                      </label>
                    </div>
                    <div className="col-span-2 pt-5 text-right">
                      {q.type !== "recommendation" && (
                        <Button size="sm" variant="ghost" onClick={() => removeQuestion(idx)} data-testid={`tpl-q-del-${idx}`}>
                          <Trash size={14}/>
                        </Button>
                      )}
                    </div>
                  </div>
                  {["single_select", "multi_select"].includes(q.type) && (
                    <div>
                      <Label className="text-[10px] uppercase text-zinc-500">Options (comma-separated)</Label>
                      <Input
                        value={(q.options || []).join(", ")}
                        onChange={(e) => updateQuestion(idx, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                        placeholder="e.g. Yes, Negotiable, No"
                        data-testid={`tpl-q-opts-${idx}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="tpl-save">{saving ? "Saving…" : "Save template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------- Plans panel ---------------------- */
function PlansPanel() {
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api.get("/interview-loop/plans"),
        api.get("/interview-loop/scorecard-templates"),
      ]);
      setPlans(p.data);
      setTemplates(t.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSaved = (p, isNew) => {
    setPlans(ps => isNew ? [...ps, p] : ps.map(x => x.id === p.id ? p : x));
    setEditing(null);
    toast.success(`Plan ${isNew ? "created" : "saved"}`);
  };
  const onDeleted = async (p) => {
    if (!window.confirm(`Archive "${p.name}"?`)) return;
    try {
      await api.delete(`/interview-loop/plans/${p.id}`);
      setPlans(ps => ps.filter(x => x.id !== p.id));
      toast.success("Plan archived");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <Card className="p-6 text-sm text-zinc-500">Loading…</Card>;

  return (
    <Card className="p-6" data-testid="plans-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold">Interview plans</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Reusable round blueprints. Attach a plan to a requisition to standardize the loop.
          </p>
        </div>
        <Button onClick={() => setEditing("new")} disabled={!templates.length} data-testid="plan-new-btn">
          <Plus size={16} className="mr-1"/> New plan
        </Button>
      </div>
      {plans.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center border border-dashed rounded">
          No plans yet. Create one to standardize how you interview for a role family.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b">
              <th className="pb-2 font-medium">Name</th>
              <th className="pb-2 font-medium">Rounds</th>
              <th className="pb-2 font-medium">Description</th>
              <th className="pb-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {plans.map(p => (
              <tr key={p.id} className="border-b last:border-0" data-testid={`plan-row-${p.id}`}>
                <td className="py-3 pr-2"><div className="font-medium">{p.name}</div></td>
                <td className="py-3 pr-2">
                  <div className="flex flex-wrap gap-1">
                    {(p.rounds || []).map((r, idx) => (
                      <span key={r.id || idx} className={`text-[11px] px-2 py-0.5 rounded ${ROUND_TYPE_COLOR[r.round_type] || ROUND_TYPE_COLOR.other}`}>
                        {idx + 1}. {r.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-3 pr-2 text-xs text-zinc-500 truncate max-w-sm">{p.description}</td>
                <td className="py-3 text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`plan-edit-${p.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => onDeleted(p)} data-testid={`plan-del-${p.id}`}><Trash size={14}/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <PlanEditor
          plan={editing === "new" ? null : editing}
          templates={templates}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </Card>
  );
}

function PlanEditor({ plan, templates, onClose, onSaved }) {
  const isNew = !plan;
  const [name, setName] = useState(plan?.name || "");
  const [description, setDescription] = useState(plan?.description || "");
  const [rounds, setRounds] = useState(() =>
    plan?.rounds ? JSON.parse(JSON.stringify(plan.rounds)) : []
  );
  const [saving, setSaving] = useState(false);

  const addRound = () => {
    const defaultTpl = templates[0];
    setRounds(rs => [...rs, {
      id: undefined,
      name: `Round ${rs.length + 1}`,
      round_type: defaultTpl?.round_type || "other",
      duration_min: 45,
      scorecard_template_id: defaultTpl?.id || "",
      description: "",
      eligible_role_filter: "",
    }]);
  };
  const removeRound = (idx) => setRounds(rs => rs.filter((_, i) => i !== idx));
  const updateRound = (idx, patch) => setRounds(rs => rs.map((r, i) => {
    if (i !== idx) return r;
    const next = { ...r, ...patch };
    // Auto-set round_type from selected template
    if (patch.scorecard_template_id) {
      const tpl = templates.find(t => t.id === patch.scorecard_template_id);
      if (tpl) next.round_type = tpl.round_type;
    }
    return next;
  }));
  const moveRound = (idx, dir) => {
    setRounds(rs => {
      const next = [...rs];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= next.length) return rs;
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (rounds.length === 0) { toast.error("Add at least one round"); return; }
    for (const r of rounds) {
      if (!r.name?.trim()) { toast.error("Every round needs a name"); return; }
      if (!r.scorecard_template_id) { toast.error(`"${r.name}" needs a scorecard template`); return; }
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description || null, rounds, is_active: true };
      const { data } = isNew
        ? await api.post("/interview-loop/plans", payload)
        : await api.put(`/interview-loop/plans/${plan.id}`, payload);
      onSaved(data, isNew);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="plan-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? "New interview plan" : `Edit — ${plan.name}`}</DialogTitle>
          <DialogDescription>
            Set the rounds in order. Each round uses a scorecard template that interviewers fill out.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Backend Engineer L3 loop" data-testid="plan-name" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="plan-desc" />
          </div>
          <div className="border border-zinc-200 rounded">
            <div className="flex items-center justify-between p-3 border-b bg-zinc-50">
              <div className="text-sm font-medium">Rounds ({rounds.length})</div>
              <Button size="sm" variant="outline" onClick={addRound} data-testid="plan-add-round"><Plus size={14} className="mr-1"/> Add round</Button>
            </div>
            <div className="divide-y">
              {rounds.map((r, idx) => (
                <div key={idx} className="p-3 space-y-2" data-testid={`plan-round-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-1 pt-6 text-center">
                      <div className="flex flex-col gap-1">
                        <button onClick={() => moveRound(idx, -1)} disabled={idx === 0} className="text-xs text-zinc-500 disabled:opacity-30">▲</button>
                        <button onClick={() => moveRound(idx, 1)} disabled={idx === rounds.length-1} className="text-xs text-zinc-500 disabled:opacity-30">▼</button>
                      </div>
                    </div>
                    <div className="col-span-4">
                      <Label className="text-[10px] uppercase text-zinc-500">Round name</Label>
                      <Input value={r.name} onChange={(e) => updateRound(idx, { name: e.target.value })} data-testid={`plan-round-name-${idx}`} />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-[10px] uppercase text-zinc-500">Scorecard template</Label>
                      <select
                        value={r.scorecard_template_id}
                        onChange={(e) => updateRound(idx, { scorecard_template_id: e.target.value })}
                        className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded text-sm"
                        data-testid={`plan-round-tpl-${idx}`}
                      >
                        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase text-zinc-500">Duration (min)</Label>
                      <Input type="number" min={15} max={240} value={r.duration_min}
                        onChange={(e) => updateRound(idx, { duration_min: parseInt(e.target.value, 10) || 45 })}
                        data-testid={`plan-round-dur-${idx}`} />
                    </div>
                    <div className="col-span-1 pt-6 text-right">
                      <Button size="sm" variant="ghost" onClick={() => removeRound(idx)} data-testid={`plan-round-del-${idx}`}>
                        <Trash size={14}/>
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-zinc-500">Eligible interviewers (hint)</Label>
                    <Input value={r.eligible_role_filter || ""} onChange={(e) => updateRound(idx, { eligible_role_filter: e.target.value })} placeholder="e.g. senior engineers, eng managers" data-testid={`plan-round-elig-${idx}`} />
                  </div>
                </div>
              ))}
              {rounds.length === 0 && (
                <div className="p-6 text-center text-sm text-zinc-500">Add rounds to build the loop.</div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="plan-save">{saving ? "Saving…" : "Save plan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
